import { useEffect, useMemo } from "react";
import * as THREE from "three";
import { useHistoryStore } from "../stores/historyStore";
import type { HeatmapCell } from "../api/rest";
import { fetchHeatmap } from "../api/rest";
import { useWarehouseId } from "../hooks/useWarehouseId";
import { useFloorFrame } from "../hooks/useFloorFrame";
import { HeatField, buildHeatLut, getLiveHeatField, HEAT_STOPS, SINK_HZ } from "./heatField";

interface Props {
  siteId: string;
}

const LIVE_REPAINT_MS = 1_000;
/** History fetch granularity — fine enough that the splat kernel does the smoothing. */
const HISTORY_CELL_M = 0.5;

export function HeatmapLayer({ siteId }: Props) {
  const mode        = useHistoryStore((s) => s.mode);
  const rangeFrom   = useHistoryStore((s) => s.rangeFrom);
  const rangeTo     = useHistoryStore((s) => s.rangeTo);
  const warehouseId = useWarehouseId();
  // The floor's real extent, not the bundled demo seed's -- see useFloorFrame.
  const { originX, originY, widthM, heightM } = useFloorFrame(siteId, warehouseId);

  const lut = useMemo(() => buildHeatLut(HEAT_STOPS), []);

  const { texture, pixels, gw, gh } = useMemo(() => {
    const f = getLiveHeatField(widthM, heightM, originX, originY); // dims authority for both modes
    const pixels = new Uint8ClampedArray(f.gw * f.gh * 4);
    const texture = new THREE.DataTexture(pixels, f.gw, f.gh, THREE.RGBAFormat, THREE.UnsignedByteType);
    texture.magFilter = THREE.LinearFilter;   // bilinear between 0.25 m texels — smooth field
    texture.minFilter = THREE.LinearFilter;
    texture.colorSpace = THREE.SRGBColorSpace;
    texture.needsUpdate = true;
    return { texture, pixels, gw: f.gw, gh: f.gh };
  }, [widthM, heightM, originX, originY]);

  useEffect(() => () => texture.dispose(), [texture]);

  // Live: repaint from the shared dwell field (fed at delta rate in Warehouse).
  useEffect(() => {
    if (mode !== "live") return;
    const repaint = () => {
      getLiveHeatField(widthM, heightM, originX, originY).colorize(pixels, lut, Date.now(), true);
      texture.needsUpdate = true;
    };
    repaint();
    const timer = setInterval(repaint, LIVE_REPAINT_MS);
    return () => clearInterval(timer);
  }, [mode, widthM, heightM, originX, originY, pixels, lut, texture]);

  // History: splat the ClickHouse aggregate through the same pipeline.
  useEffect(() => {
    if (mode !== "history" || !rangeFrom || !rangeTo) return;
    let cancelled = false;
    fetchHeatmap(siteId, rangeFrom, rangeTo, HISTORY_CELL_M, warehouseId)
      .then((cells: HeatmapCell[]) => {
        if (cancelled) return;
        const f = new HeatField(widthM, heightM, 0.25, originX, originY);
        for (const c of cells) f.splat(c.x, c.z, c.count / SINK_HZ, rangeTo);
        f.colorize(pixels, lut, rangeTo, true);
        texture.needsUpdate = true;
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [mode, siteId, warehouseId, rangeFrom, rangeTo, widthM, heightM, originX, originY,
      pixels, lut, texture]);

  void gw; void gh;

  return (
    <mesh rotation={[-Math.PI / 2, 0, 0]}
          position={[originX + widthM / 2, 0.02, originY + heightM / 2]}>
      <planeGeometry args={[widthM, heightM]} />
      <meshBasicMaterial map={texture} transparent depthWrite={false} side={THREE.DoubleSide} toneMapped={false} />
    </mesh>
  );
}
